<template lang="pug">
  .make-an-entry-sleep
    section.section.pb-1.pt-2.mt-2
      MakeAnEntryVisibility(text="Always Private")
      WtTitle(h4 bold style="margin-bottom:5px") Sleep
      WtLabel(
        style="color:#756E7A"
        text="Enter your time into and out of bed to calculate your total time asleep."
      )
      .make-an-entry-sleep-body.pt-4
        BField.mx-3(label="Into Bed")
          .is-flex.is-align-items-center
            WtSvgIcon.mr-4(icon="icon_into_bed" width="32px" height="32px")
            BField
              BTimepicker(
                size="is-small"
                hour-format="12"
                inline
                v-model="startTime"
                :default-minutes="0"
              )
        BField.mx-3(label="Out of Bed")
          .is-flex.is-align-items-center
            WtSvgIcon.mr-4(icon="icon_out_of_bed" width="32px" height="32px")
            BField
              BTimepicker(
                size="is-small"
                hour-format="12"
                inline
                v-model="endTime"
                :default-minutes="30"
              )
      .block.ml-3.mt-2
        span.is-size-5.has-text-weight-bold Total Sleep Time: &nbsp;
          span.has-text-weight-light {{ totalSleepTimeText }}
      MakeAnEntryTip.ml-5(:tip-category="'sleep'")
    .level.is-mobile.program-settings-form-controls
      .level-left
      .level-right
        .level-item
          WtButton(
            is-text
            color="white"
            style="text-decoration:none"
            size="medium"
            @click.native="reset"
            :disabled="disableReset"
          ) Reset
        .level-item
          WtButton(@click.native="saveEntry" :loading="savingSleep" :disabled="disableSave") SAVE
</template>

<script>
import { mapActions } from "vuex";
import MakeAnEntryFooter from "@@/Members/MakeAnEntry/MakeAnEntryFooter";
import MakeAnEntryVisibility from "@@/Members/MakeAnEntry/MakeAnEntryVisibility";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import MakeAnEntryTip from "@@/Members/MakeAnEntry/MakeAnEntryTip";
import WtLabel from "@@/WtLabel/WtLabel";

export default {
  name: "MakeAnEntrySleep",

  components: {
    WtTitle,
    WtLabel,
    WtButton,
    MakeAnEntryFooter,
    WtSvgIcon,
    MakeAnEntryTip,
    MakeAnEntryVisibility
  },

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    fetchAction: {
      type: Function,
      default: () => {},
      required: true
    },

    entryItem: {
      type: Object,
      default: () => {},
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      startTime: new Date(new Date().setHours(22, 30, 0)),
      endTime: new Date(new Date().setHours(6, 30, 0)),
      savingSleep: false
    };
  },

  computed: {
    sleepHourMinutes() {
      const startObj = this.moment(this.startTime);
      const endObj = this.moment(this.endTime);
      let hourDiff = endObj.hours() - startObj.hours();
      let minDiff = endObj.minutes() - startObj.minutes();

      if (hourDiff < 0) hourDiff = 24 + hourDiff;
      else if (hourDiff === 0 && minDiff < 0) hourDiff = 23;

      if (minDiff < 0) {
        minDiff = 60 + minDiff;
        hourDiff = hourDiff - 1;
      }
      return { hourDiff, minDiff };
    },

    totalSleepTime() {
      return parseFloat(
        // sends sleep time to db as total minutes
        (
          this.sleepHourMinutes.hourDiff * 60 +
          this.sleepHourMinutes.minDiff
        ).toString()
      ).toFixed(2);
    },

    totalSleepTimeText() {
      return `${this.sleepHourMinutes.hourDiff} hrs, ${this.sleepHourMinutes.minDiff} min`;
    },

    currentMetric() {
      return this._.find(this.entryItem.wellnessMetrics, {
        type: "sleep"
      });
    },

    currentMetricValue() {
      return this._.get(this.currentMetric, "value", 0);
    },

    currentMetricText() {
      return this._.get(this.currentMetric, "text", "");
    },

    currentStartTime() {
      return this.moment(
        this._.get(this.currentMetricText.split(","), "[0]", "")
      )._d;
    },

    currentEndTime() {
      return this.moment(
        this._.get(this.currentMetricText.split(","), "[1]", "")
      )._d;
    },

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        // either it's changed or it might have been changed and changed back to what it was
        return (
          this.startTime !== this.currentStartTime ||
          this.endTime !== this.currentEndTime ||
          this.formData.has("sleep")
        );
      }
      return true;
    }
  },

  watch: {
    totalSleepTime() {
      if (this.setFormData && !this.resettingForm) {
        this.formData.set("sleep", this.totalSleepTime);
        this.formData.set("sleepStart", this.startTime);
        this.formData.set("sleepEnd", this.endTime);
        this.$emit("formDataUpdated", true);
      }
    }
  },

  mounted() {
    if (this.currentMetricText && this.currentMetricText.includes(",")) {
      this.startTime = this.currentStartTime;
      this.endTime = this.currentEndTime;
    }
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),

    saveEntry() {
      this.savingSleep = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal);
    },

    reset() {
      const items = ["sleep", "sleepStart", "sleepEnd"];
      for (const el of items) {
        this.formData.delete(el);
      }
      this.startTime = new Date(new Date().setHours(22, 30, 0));
      this.endTime = new Date(new Date().setHours(6, 30, 0));
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass">
.make-an-entry-sleep
  .wellness-visibility
    position: absolute
    right: 1rem
    top: .5rem
  .make-an-entry-sleep-body
    display: flex
    justify-content: space-between
    flex-wrap: wrap
    width: 100%
    max-width: 520px
.program-settings-form-controls
  margin-top: 20px
</style>
